import React, { type ReactNode } from 'react';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Clock3,
  Inbox,
  LoaderCircle,
  RefreshCw,
  ShieldX,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  EXPERIENCE_HEADING_CLASS,
  EXPERIENCE_MOTION_CLASS,
  EXPERIENCE_STATUS_TONE_CLASSES,
  type ExperienceStatusTone,
} from '../../lib/experience-semantic-styles';

export const EXPERIENCE_STATE_NAMES = [
  'loading',
  'ready',
  'empty',
  'denied',
  'unavailable',
  'stale',
  'error',
  'action_pending',
  'action_committed',
  'action_failed',
  'superseded',
] as const;

export type ExperienceStateName = (typeof EXPERIENCE_STATE_NAMES)[number];

const presentation = {
  loading: {
    Icon: LoaderCircle,
    tone: 'information',
    legacyTone: 'text-status-information bg-status-information/10 border-status-information',
    live: 'polite',
  },
  ready: {
    Icon: CheckCircle2,
    tone: 'success',
    legacyTone: 'text-status-success bg-status-success/10 border-status-success',
    live: 'polite',
  },
  empty: {
    Icon: Inbox,
    tone: 'neutral',
    legacyTone: 'text-text-body bg-surface-soft border-border',
    live: 'polite',
  },
  denied: {
    Icon: ShieldX,
    tone: 'danger',
    legacyTone: 'text-status-danger bg-status-danger/10 border-status-danger',
    live: 'assertive',
  },
  unavailable: {
    Icon: Ban,
    tone: 'neutral',
    legacyTone: 'text-text-body bg-surface-soft border-border',
    live: 'polite',
  },
  stale: {
    Icon: RefreshCw,
    tone: 'warning',
    legacyTone: 'text-status-warning bg-status-warning/10 border-status-warning',
    live: 'polite',
  },
  error: {
    Icon: AlertCircle,
    tone: 'danger',
    legacyTone: 'text-status-danger bg-status-danger/10 border-status-danger',
    live: 'assertive',
  },
  action_pending: {
    Icon: LoaderCircle,
    tone: 'approval',
    legacyTone: 'text-status-information bg-status-information/10 border-status-information',
    live: 'polite',
  },
  action_committed: {
    Icon: CheckCircle2,
    tone: 'success',
    legacyTone: 'text-status-success bg-status-success/10 border-status-success',
    live: 'polite',
  },
  action_failed: {
    Icon: AlertCircle,
    tone: 'danger',
    legacyTone: 'text-status-danger bg-status-danger/10 border-status-danger',
    live: 'assertive',
  },
  superseded: {
    Icon: Clock3,
    tone: 'neutral',
    legacyTone: 'text-text-body bg-surface-soft border-border',
    live: 'polite',
  },
} as const satisfies Record<
  ExperienceStateName,
  {
    Icon: typeof LoaderCircle;
    tone: ExperienceStatusTone;
    legacyTone: string;
    live: 'polite' | 'assertive';
  }
>;

export interface ExperienceStateProps {
  state: ExperienceStateName;
  title: string;
  description: string;
  actions?: ReactNode;
  className?: string;
  compact?: boolean;
  titleLevel?: 2 | 3;
}

export function ExperienceState({
  state,
  title,
  description,
  actions,
  className,
  compact = false,
  titleLevel = 2,
}: ExperienceStateProps) {
  const { Icon, tone, legacyTone, live } = presentation[state];
  const isBusy = state === 'loading' || state === 'action_pending';
  const isError = state === 'denied' || state === 'error' || state === 'action_failed';
  const Title = titleLevel === 3 ? 'h3' : 'h2';

  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        legacyTone,
        EXPERIENCE_STATUS_TONE_CLASSES[tone],
        EXPERIENCE_MOTION_CLASS,
        compact ? 'p-4' : 'min-h-40',
        className,
      )}
      role={isError ? 'alert' : 'status'}
      aria-live={live}
      aria-busy={isBusy || undefined}
      data-experience-state={state}
      data-experience-tone={tone}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            'experience-candidate-motion mt-0.5 h-5 w-5 shrink-0',
            isBusy && 'animate-spin motion-reduce:animate-none',
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <Title
            className={cn('font-heading text-card-title text-current', EXPERIENCE_HEADING_CLASS)}
          >
            {title}
          </Title>
          <p className="experience-candidate-body mt-1 font-body text-body-sm text-current/90">
            {description}
          </p>
          {actions ? <div className="mt-4 flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      </div>
    </section>
  );
}
